import { EmbeddedProviderQueryParam } from '../config/constants';

export interface EmbeddedProviderSetup {
  paymentSessionId: string;
  authToken: string;
  requestContext: { piid?: string; orderId?: string };
}

export interface EmbeddedProviderSetupState {
  setup: EmbeddedProviderSetup | null;
  parentOrigin: string | null;
  failed: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && !!value.trim();

export class EmbeddedProviderSetupReceiver {
  private state: EmbeddedProviderSetupState = { setup: null, parentOrigin: null, failed: false };
  private listeners = new Set<() => void>();
  private stopped = false;

  constructor(private target: Window, private sessionId: string, private usePaymentClient = false) {
    target.addEventListener('message', this.receive);
    target.addEventListener('pagehide', this.dispose);
  }

  getSnapshot = (): EmbeddedProviderSetupState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  dispose = (): void => {
    this.target.removeEventListener('message', this.receive);
    this.target.removeEventListener('pagehide', this.dispose);
    this.stopped = true;
    this.state = { setup: null, parentOrigin: null, failed: false };
    this.listeners.clear();
    if (receiver === this) receiver = undefined;
  };

  private receive = (event: MessageEvent): void => {
    if (this.stopped || event.source !== this.target.parent || this.state.setup || this.state.failed) return;
    const message: unknown = event.data;
    if (!isRecord(message) || message.source !== 'PidlSdk' || message.messageType !== 'iframe.setup') return;
    const data = message.iFrameData;
    const serviceOverrides = isRecord(data) && isRecord(data.serviceOverrides) ? data.serviceOverrides : undefined;
    const pidlOverride = serviceOverrides && isRecord(serviceOverrides.pidl) ? serviceOverrides.pidl : undefined;
    const authToken = pidlOverride?.authToken;
    const requestContext = isRecord(data) && isRecord(data.requestContext) ? data.requestContext : undefined;
    const piid = requestContext?.piid;
    if (!isRecord(data)
      || data.paymentSessionId !== this.sessionId
      || !isNonEmptyString(authToken)
      || !requestContext
      || (!this.usePaymentClient && !isNonEmptyString(piid))
      || (piid !== undefined && !isNonEmptyString(piid))
      || (requestContext.orderId !== undefined && typeof requestContext.orderId !== 'string')
      || !event.origin || event.origin === 'null') {
      console.warn('[EmbeddedProvider] Invalid iframe.setup message.');
      this.state = { setup: null, parentOrigin: event.origin || null, failed: true };
    } else {
      this.state = {
        setup: {
          paymentSessionId: this.sessionId,
          authToken,
          requestContext: {
            ...(isNonEmptyString(piid) ? { piid } : {}),
            ...(typeof requestContext.orderId === 'string' ? { orderId: requestContext.orderId } : {}),
          },
        },
        parentOrigin: event.origin,
        failed: false,
      };
    }
    this.listeners.forEach(listener => listener());
  };
}

let receiver: EmbeddedProviderSetupReceiver | undefined;

export function installEmbeddedProviderSetupReceiver(): EmbeddedProviderSetupReceiver | undefined {
  const url = new URL(window.location.href);
  const sessionId = url.searchParams.get(EmbeddedProviderQueryParam.PaymentSessionId);
  if (!receiver && url.pathname.startsWith('/embeddedprovider') && sessionId?.trim() &&
    url.searchParams.get(EmbeddedProviderQueryParam.IsFramed) === 'true' && window.parent !== window) {
    receiver = new EmbeddedProviderSetupReceiver(
      window,
      sessionId,
      url.searchParams.get(EmbeddedProviderQueryParam.UsePaymentClient) === 'true',
    );
  }
  return receiver;
}
